import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as LineChartIcon } from "../../assets/icons/bar-line-chart.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendar-blank.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/clock-01.svg";
import { ReactComponent as DropletsIcon } from "../../assets/icons/droplets-02.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/mail-01.svg";
import { PageContainer, PageHeader } from "../../components/AssetBreadCrumb";
import { LinkCardGrid, LinkCardItem } from "../../components/ReusableComponents/LinkCardGrid";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

// Self-serve tools for every signed-in user, one card each, on the same grid as Resources so the two pages read as one. Each tool lives at its own /home/* route.
const ToolsPage: React.FC = () => {
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();

  useEffect(() => {
    capture(POSTHOG_EVENTS.TOOLS_PAGE_VIEWED);
    // Fire once on mount — capture identity is not stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tools: LinkCardItem[] = [
    {
      id: "email-schedules",
      title: "Email Schedules",
      description: "Schedule regular emails to your inbox to stay up-to-date on the latest forecasts.",
      badge: "Beta",
      Icon: MailIcon,
      cta: "Manage schedules",
      onSelect: () => navigate("/home/email-schedule"),
    },
    {
      id: "delivery-logs",
      title: "Delivery Logs",
      description: "See when each of your forecasts was delivered, and whether it arrived on time.",
      badge: "Beta",
      Icon: ClockIcon,
      cta: "View delivery logs",
      onSelect: () => navigate("/home/delivery-logs"),
    },
    {
      id: "forecast-verification",
      title: "Forecast Verification Reporting",
      description: "Score a forecast against actuals by lead time — MAE, RMSE and bias by week, with actual-vs-forecast and error plots.",
      badge: "Beta",
      Icon: LineChartIcon,
      cta: "Open reports",
      onSelect: () => navigate("/home/forecast-verification"),
    },
    {
      id: "watershed-delineation",
      title: "Watershed Delineation",
      description: "Click a point on a stream to trace its drainage basin, save it as GeoJSON or KML, and assign it to an asset.",
      badge: "Beta",
      Icon: DropletsIcon,
      cta: "Open tool",
      onSelect: () => navigate("/home/watershed-delineation"),
    },
    {
      id: "orchestration",
      title: "Orchestration",
      description: "See when every deployed model is scheduled to run, and which models feed which.",
      badge: "Beta",
      Icon: CalendarIcon,
      cta: "Open orchestration",
      onSelect: () => navigate("/home/orchestration"),
    },
  ];

  return (
    <PageContainer>
      <PageHeader eyebrow="Tools" title="Tools" />
      <LinkCardGrid items={tools} />
    </PageContainer>
  );
};

export default ToolsPage;
